'use memo'

import React from 'react'
import { IdcardOutlined } from '@ant-design/icons'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { TextField, FieldRow } from '../FormikField'
import EditableSection from '../EditableSection'

interface Props {
  /**
   * Identifiers (Customer Identifier + Canlan's Salesforce ID). Shown in the
   * roster player form only — the profile Edit Player modal leaves this off and
   * never renders them.
   */
  showIdentifiers?: boolean
  /** Admin-only, and only alongside `showIdentifiers`. */
  showCustomerIdentifier?: boolean
  /** Read-only only in add-existing (copy); editable on create + edit. */
  salesforceIdLocked?: boolean
}

const MembershipSection: React.FC<Props> = ({
  showIdentifiers = false,
  showCustomerIdentifier = false,
  salesforceIdLocked = false,
}) => (
  <EditableSection
    id='membership'
    title={capitalizeWords('Membership')}
    icon={<IdcardOutlined />}
  >
    {/* Identifiers get their own row ONLY when both render (Canlan). On
        SportNinja there's no Salesforce ID, so a lone Customer Identifier would
        leave half the row empty — it pairs with Loyalty Number below instead. */}
    {showIdentifiers && showCustomerIdentifier && isCanlan ? (
      <FieldRow cols={2}>
        <TextField
          name='customerIdentifier'
          label='Customer Identifier'
          placeholder='e.g. 38183811'
        />
        {/* Salesforce ID — Canlan only (parity with the legacy roster form). */}
        <TextField
          name='salesforceId'
          label='Salesforce ID'
          placeholder='e.g. 0010Z00002agcJEFAY'
          disabled={salesforceIdLocked}
        />
      </FieldRow>
    ) : null}
    {/* Canlan without admin rights: Salesforce ID alone still needs a row. */}
    {showIdentifiers && !showCustomerIdentifier && isCanlan ? (
      <FieldRow cols={2}>
        <TextField
          name='salesforceId'
          label='Salesforce ID'
          placeholder='e.g. 0010Z00002agcJEFAY'
          disabled={salesforceIdLocked}
        />
      </FieldRow>
    ) : null}
    <FieldRow>
      <TextField
        name='leagueRegistrationNumber'
        label='League Registration Number'
        placeholder={capitalizeWords('Registration number')}
      />
      <TextField
        name='hockeyUsaNumber'
        label='USA Hockey Number'
        placeholder={capitalizeWords('USA Hockey number')}
      />
    </FieldRow>
    <FieldRow>
      <TextField
        name='hockeyCanadaNumber'
        label='Hockey Canada Number'
        placeholder={capitalizeWords('Hockey Canada number')}
      />
      <TextField
        name='customMembershipNumber'
        label='Custom Membership Number'
        placeholder={capitalizeWords('Membership number')}
      />
    </FieldRow>
    <FieldRow>
      <TextField
        name='loyaltyNumber'
        label='Loyalty Number'
        placeholder={capitalizeWords('Loyalty number')}
      />
      {/* SportNinja (no Salesforce ID): Customer Identifier fills this row's
          empty second cell rather than sitting alone above. */}
      {showIdentifiers && showCustomerIdentifier && !isCanlan ? (
        <TextField
          name='customerIdentifier'
          label='Customer Identifier'
          placeholder='e.g. 38183811'
        />
      ) : null}
    </FieldRow>
  </EditableSection>
)

export default MembershipSection
